import { Avatar, Box, Divider, Drawer, IconButton, List, useMediaQuery, useTheme } from '@mui/material';
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import VListItem from './VListItem';
import { snakeCase } from 'lodash-es';
import verticalHeaderOptions from '../../config/verticalHeaderOptions';
import { Close } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../store/auth/authActions';
import { useTranslation } from 'react-i18next';
import findLocalsOrUseFirst from '../../utils/findLocalsOrUseFirst';

const VerticalHeader = ({ option = 'dashboard', archives }) => {
  const theme = useTheme();
  const { i18n, t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('lg'));
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const user = useSelector((state) => state?.auth?.currentUser);

  const path = pathname.split('/').filter((e) => e);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const toggleDropdown = (label) => {
    setOpenDropdown((prev) => (prev === label ? null : label));
  };

  const isActive = (label, navigateTo, isDropdownOption = false) => {
    const currentPath = snakeCase(path[path.length - 1]);
    const currentLabel = snakeCase(label);

    if (isDropdownOption) return navigateTo.endsWith(currentPath);
    if (currentLabel === 'archive') return path.includes(currentLabel);

    if (label?.startsWith('my_documents/')) {
      const archiveName = label.split('/')[1];
      return currentPath === snakeCase(archiveName);
    }

    return currentPath === currentLabel;
  };

  const listArr = verticalHeaderOptions?.[option]?.topArr;
  const bottomArr = verticalHeaderOptions?.[option]?.bottomArr;
  const link = verticalHeaderOptions?.[option]?.link;

  const userActive = useSelector((state) => state.auth);
  const isAdmin = userActive.roles?.includes('admin');
  const isBroker = userActive.roles?.includes('broker');

  // Colors pulled from theme
  const drawerBg = theme.palette.background.alt || theme.palette.grey[100];
  const headerBg = theme.palette.primary.main;
  const iconColor = theme.palette.common.white;

  const mainColor = theme.palette.primary.main;
  console.log(mainColor, 'MAINCOLOR');

  return (
    <>
      {isMobile ? (
        <>
          {/* MOBILE STICKY HEADER */}
          <Box
            overflow="hidden"
            display="flex"
            bgcolor={headerBg}
            color={theme.palette.common.white}
            zIndex={1000}
            px={1}
            width="100%"
            top="0"
            position="sticky"
            justifyContent="space-between"
            boxShadow={theme.shadows[4]}
          >
            <Link to={link}>
              <Box
                component="img"
                margin="15px"
                sx={{
                  height: { xs: '33px' },
                  width: { xs: '180px' },
                  objectFit: 'contain',
                }}
                alt="Your logo."
                src="/FinowLogoWhite.svg"
              />
            </Link>

            <IconButton onClick={handleDrawerToggle} sx={{ color: iconColor }}>
              <MenuIcon />
            </IconButton>
          </Box>

          {/* MOBILE DRAWER */}
          <Drawer
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: 'block', lg: 'none' },
              '& .MuiDrawer-paper': {
                width: '260px',
                bgcolor: drawerBg,
                boxSizing: 'border-box',
              },
            }}
            variant="temporary"
            anchor="right"
          >
            <Box display="flex" alignItems="center" justifyContent="space-between" pl={2} pr={1} py={1.5}>
              <Box display="flex" alignItems="center" gap="10px">
                <Avatar />
                {user?.firstName}
              </Box>
              <IconButton onClick={handleDrawerToggle}>
                <Close />
              </IconButton>
            </Box>

            <Divider />

            <Box display="flex" flexDirection="column" justifyContent="space-between" height="100%">
              <List>
                {listArr?.map((post, i) =>
                  post.dropdownoptions ? (
                    <React.Fragment key={i}>
                      <VListItem
                        handleClose={handleDrawerToggle}
                        isActive={isActive(post?.navigateTo)}
                        label={t(post?.label)}
                        onClick={() => toggleDropdown(post.label)}
                        {...post}
                      />
                      {openDropdown === post.label &&
                        post.dropdownoptions.map((dropdownOption, j) => (
                          <VListItem
                            handleClose={handleDrawerToggle}
                            key={j}
                            isActive={isActive(dropdownOption?.label, dropdownOption?.navigateTo, true)}
                            label={t(dropdownOption?.label)}
                            style={{ marginLeft: 10 }}
                            {...dropdownOption}
                          />
                        ))}
                    </React.Fragment>
                  ) : (
                    <VListItem
                      handleClose={handleDrawerToggle}
                      key={i}
                      isActive={isActive(post?.label)}
                      label={t(post?.label)}
                      {...post}
                    />
                  ),
                )}

                {/* ARCHIVES */}
                {archives
                  ?.filter((arch) => arch.name !== 'articles')
                  ?.map((archive, idx) => (
                    <VListItem
                      handleClose={handleDrawerToggle}
                      key={idx}
                      isActive={isActive(`my_documents/${archive?.name}`)}
                      label={findLocalsOrUseFirst(archive?.locals, i18n.language)?.name}
                      imgSrc={archive.icon}
                      navigateTo={`my_documents/${archive.name}`}
                      auth
                    />
                  ))}
              </List>

              <List>
                <Divider />

                {bottomArr?.map((post, i) => (
                  <VListItem
                    handleClose={handleDrawerToggle}
                    key={i}
                    isActive={isActive(post?.label)}
                    label={t(post?.label)}
                    {...post}
                  />
                ))}

                <VListItem
                  onClick={() => dispatch(logout())}
                  label="Logout"
                  imgSrc="https://taxofficestorage.blob.core.windows.net/media/logout.svg"
                />
              </List>
            </Box>
          </Drawer>
        </>
      ) : (
        /* DESKTOP */
        <Drawer
          sx={{
            width: '260px',
            flexShrink: 0,
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              width: '260px',
              bgcolor: mainColor,
              boxSizing: 'border-box',
            },
          }}
          variant="permanent"
          anchor="left"
        >
          <Link to={isAdmin ? '/admin/dashboard' : isBroker ? '/broker/dashboard' : link}>
            <Box
              component="img"
              margin="20px"
              sx={{
                height: { xs: '28px', md: '35px' },
                width: { xs: '168px', md: '205px' },
                objectFit: 'contain',
              }}
              alt="Your logo."
              src="/FinowLogoWhite.svg"
            />
          </Link>

          <Divider />

          <Box display="flex" flexDirection="column" justifyContent="space-between" height="100%">
            <List>
              {listArr?.map((post, i) =>
                post.dropdownoptions ? (
                  <React.Fragment key={i}>
                    <VListItem
                      isActive={isActive(post?.navigateTo)}
                      label={t(post?.label)}
                      onClick={() => toggleDropdown(post.label)}
                      {...post}
                    />
                    {openDropdown === post.label &&
                      post.dropdownoptions.map((dropdownOption, j) => (
                        <VListItem
                          key={j}
                          isActive={isActive(dropdownOption?.label, dropdownOption?.navigateTo, true)}
                          label={t(dropdownOption?.label)}
                          style={{ marginLeft: 10 }}
                          {...dropdownOption}
                        />
                      ))}
                  </React.Fragment>
                ) : (
                  <VListItem key={i} isActive={isActive(post?.label)} label={t(post?.label)} {...post} />
                ),
              )}

              {archives
                ?.filter((arch) => arch.name !== 'articles')
                ?.map((archive, idx) => (
                  <VListItem
                    key={idx}
                    isActive={isActive(`my_documents/${archive?.name}`)}
                    label={findLocalsOrUseFirst(archive?.locals, i18n.language)?.name}
                    imgSrc={archive.icon}
                    navigateTo={`my_documents/${archive.name}`}
                    auth
                  />
                ))}
            </List>

            <List>
              <Divider />

              {bottomArr?.map((post, i) => (
                <VListItem key={i} isActive={isActive(post?.label)} label={t(post?.label)} {...post} />
              ))}

              <VListItem
                onClick={() => dispatch(logout())}
                label="Logout"
                imgSrc="https://taxofficestorage.blob.core.windows.net/media/logout.svg"
              />
            </List>
          </Box>
        </Drawer>
      )}
    </>
  );
};

export default VerticalHeader;
